/**
 * ManualDiagrams — "Diagrams from the manual" PDP section (FRIDGE-280
 * step 2/3: photos from manuals — dimension drawings, installation and parts
 * diagrams no retailer shows, extracted from the manufacturer documents we
 * already hold and served from OUR storage).
 *
 * Data source:
 *   - canonical_products.manual_diagrams jsonb, resolved server-side by
 *     lib/render-policy/display-manual-diagrams getDisplayManualDiagrams.
 *   - Every src the resolver emits is the fridge.com-proxied
 *     /product-images/manual-diagrams/... path — an absolute URL, raw storage
 *     host, or third-party host cannot reach this component's output (the
 *     resolver's SAFE_PATH_RE fails closed).
 *
 * Placement: right below the Technical Specs card — the dimension drawing is
 * the visual twin of the specs table (card step 3: "dimension drawings beside
 * the specs table"). Rendered in both TechnicalSpecs instances' containers,
 * so it follows specs on desktop (gallery column) and mobile alike; pass
 * idSuffix to keep anchors unique, matching the specifications /
 * specifications-mobile convention.
 *
 * Real-or-Hidden Rule 3: null resolver output → null. Section absent.
 */

import Image from 'next/image'
import Link from 'next/link'
import { BookOpen, ArrowUpRight } from 'lucide-react'
import type { DisplayManualDiagrams } from '@/lib/render-policy/display-manual-diagrams'

interface ManualDiagramsProps {
  diagrams: DisplayManualDiagrams | null | undefined
  /** '' on desktop instance, '-mobile' on the mobile-flow instance. */
  idSuffix?: string
  className?: string
}

export function ManualDiagrams({ diagrams, idSuffix = '', className = '' }: ManualDiagramsProps) {
  if (!diagrams || diagrams.assets.length === 0) return null
  const { assets, heading, eyebrow, intro, sourceLine } = diagrams
  const manualHref = assets.find((a) => a.manualHref)?.manualHref ?? null

  return (
    <section
      id={`manual-diagrams${idSuffix}`}
      className={`rounded-lg border border-gray-200 bg-white p-4 sm:p-5 scroll-mt-28 ${className}`}
      aria-label={heading}
      data-testid="manual-diagrams"
      data-diagram-count={assets.length}
    >
      <header className="mb-3">
        <p className="text-[#ff9951] font-bold tracking-widest text-[11px] uppercase mb-1.5">
          {eyebrow}
        </p>
        <p className="text-xl font-black tracking-tighter text-gray-900">{heading}</p>
      </header>
      <p className="text-sm text-gray-500 leading-relaxed mb-4">{intro}</p>

      <div className={assets.length === 1 ? '' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
        {assets.map((a) => (
          <figure
            key={a.src}
            className="rounded-lg border border-gray-100 bg-gray-50/50 p-2"
            data-testid={`manual-diagram-${a.kind}`}
          >
            <Image
              src={a.src}
              alt={a.alt}
              width={a.w}
              height={a.h}
              className="w-full h-auto rounded"
              sizes="(max-width: 640px) 100vw, 50vw"
              // FRIDGE-1540: a.w x a.h is the file's own size, so its aspect ratio inline
              // draws the same shape and Chromium counts this lazy image as sized.
              style={{ aspectRatio: `${a.w} / ${a.h}` }}
            />
            <figcaption className="mt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500">
              {a.label}
              {a.page ? ` · page ${a.page}` : ''}
            </figcaption>
          </figure>
        ))}
      </div>

      <footer className="mt-4 pt-4 border-t border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <p className="text-xs text-gray-500" data-testid="manual-diagrams-source">
          {sourceLine}
        </p>
        {manualHref && (
          <Link
            href={manualHref}
            // FRIDGE-1505 E2: a PDF is not a page; never router-prefetch it
            // (this card's link still did on 10/15/2026).
            prefetch={false}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0469f0] hover:text-[#0353c0] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0469f0]/40 rounded flex-shrink-0"
          >
            <BookOpen className="w-4 h-4" aria-hidden="true" />
            View the source document
            <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
          </Link>
        )}
      </footer>
    </section>
  )
}

export default ManualDiagrams
